<script setup lang="ts">
import { SolButton, SolModal } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import CheckCircleIcon from '~icons/material-symbols/check'

const props = withDefaults(defineProps<{
  isOpen: boolean
  mode?: 'home' | 'internal'
}>(), {
  mode: 'home',
})

const emit = defineEmits<{
  primary: []
  secondary: []
  tertiary: []
  close: []
}>()

const { t } = useI18n()

const bulletKeys = [
  'navigation',
  'search',
  'projects_panel',
  'metrics',
] as const

const primaryActionText = computed(() => {
  if (props.mode === 'internal')
    return t('overview.intro_modal.actions.view_all')
  return t('overview.intro_modal.actions.start_tour')
})

const secondaryActionText = computed(() => {
  if (props.mode === 'internal')
    return t('overview.intro_modal.actions.view_this_screen')
  return t('overview.intro_modal.actions.skip_tour')
})

const showTertiaryAction = computed(() => props.mode === 'internal')
const tertiaryActionText = computed(() => t('overview.intro_modal.actions.skip_tour'))

function handleClose() {
  emit('close')
}
</script>

<template>
  <SolModal
    id="overview-intro-modal"
    :is-open="props.isOpen"
    :title="t('overview.intro_modal.title')"
    :size="{ desktop: 'large', mobile: 'bottom-sheet' }"
    @close="handleClose"
  >
    <template #action-primary>
      <SolButton
        id="overview-intro-primary"
        variant="primary"
        size="medium"
        @click="emit('primary')"
      >
        {{ primaryActionText }}
      </SolButton>
    </template>

    <template #action-secondary>
      <SolButton
        id="overview-intro-secondary"
        :variant="props.mode === 'internal' ? 'secondary' : 'secondary'"
        size="medium"
        @click="emit('secondary')"
      >
        {{ secondaryActionText }}
      </SolButton>
    </template>

    <template v-if="showTertiaryAction" #action-tertiary>
      <SolButton
        id="overview-intro-tertiary"
        variant="tertiary"
        size="medium"
        @click="emit('tertiary')"
      >
        {{ tertiaryActionText }}
      </SolButton>
    </template>

    <div class="flex flex-wrap justify-center sm:system:justify-start sm:system:flex-nowrap gap-8">
      <div class="max-w-[400px] md:system:w-[400px] self-center">
        <img
          src="/images/onboarding-home-modal.svg"
          alt=""
        >
      </div>

      <div class="flex flex-col gap-4 md:w-2/3">
        <span
          class="fonts-body-medium-regular text-neutral-low-medium"
          v-html="t('overview.intro_modal.description')"
        />

        <ul class="flex flex-col gap-3">
          <li
            v-for="bulletKey in bulletKeys"
            :key="bulletKey"
            class="flex items-start gap-3"
          >
            <CheckCircleIcon class="mt-[2px] text-brand-primary-pure flex-shrink-0" />
            <span
              class="fonts-body-medium-regular text-neutral-low-medium"
              v-html="t(`overview.intro_modal.bullets.${bulletKey}`)"
            />
          </li>
        </ul>

        <p class="fonts-body-medium-regular text-neutral-low-medium">
          {{ t('overview.intro_modal.description_2') }}
        </p>
      </div>
    </div>
  </SolModal>
</template>
